/**
 * Approval Workflow
 * Manages approval of scripts at trust level 2 (Explicit)
 */

export interface ScriptApprovalRequest {
  scriptId: string;
  scriptName: string;
  code: string;
  requestedAt: number;
  requestedBy?: string;
  reason?: string;
}

export interface ScriptApproval {
  scriptId: string;
  approved: boolean;
  approvedAt: number;
  approvedBy?: string;
  notes?: string;
  expiresAt?: number; // Optional: approval expires after this time
}

export interface IApprovalStore {
  /**
   * Submit script for approval
   */
  requestApproval(request: ScriptApprovalRequest): Promise<void>;

  /**
   * Check if script is approved
   */
  isApproved(scriptId: string): Promise<boolean>;

  /**
   * Get approval details
   */
  getApproval(scriptId: string): Promise<ScriptApproval | null>;

  /**
   * Approve a script
   */
  approve(
    scriptId: string,
    approvedBy: string,
    notes?: string,
    expiresIn?: number
  ): Promise<void>;

  /**
   * Reject a script
   */
  reject(scriptId: string, approvedBy: string, notes?: string): Promise<void>;

  /**
   * Get pending approval requests
   */
  getPendingRequests(): Promise<ScriptApprovalRequest[]>;

  /**
   * Revoke approval
   */
  revoke(scriptId: string): Promise<void>;
}

/**
 * In-memory approval store (default)
 */
export class InMemoryApprovalStore implements IApprovalStore {
  private approvals: Map<string, ScriptApproval> = new Map();
  private pendingRequests: Map<string, ScriptApprovalRequest> = new Map();

  async requestApproval(request: ScriptApprovalRequest): Promise<void> {
    this.pendingRequests.set(request.scriptId, request);
  }

  async isApproved(scriptId: string): Promise<boolean> {
    const approval = this.approvals.get(scriptId);
    if (!approval) return false;

    // Check if approval has expired
    if (approval.expiresAt && approval.expiresAt < Date.now()) {
      this.approvals.delete(scriptId);
      return false;
    }

    return approval.approved;
  }

  async getApproval(scriptId: string): Promise<ScriptApproval | null> {
    const approval = this.approvals.get(scriptId) || null;

    // Return null if expired
    if (approval?.expiresAt && approval.expiresAt < Date.now()) {
      this.approvals.delete(scriptId);
      return null;
    }

    return approval;
  }

  async approve(
    scriptId: string,
    approvedBy: string,
    notes?: string,
    expiresIn?: number
  ): Promise<void> {
    this.approvals.set(scriptId, {
      scriptId,
      approved: true,
      approvedAt: Date.now(),
      approvedBy,
      notes,
      expiresAt: expiresIn ? Date.now() + expiresIn : undefined,
    });

    this.pendingRequests.delete(scriptId);
  }

  async reject(
    scriptId: string,
    approvedBy: string,
    notes?: string
  ): Promise<void> {
    this.approvals.set(scriptId, {
      scriptId,
      approved: false,
      approvedAt: Date.now(),
      approvedBy,
      notes,
    });

    this.pendingRequests.delete(scriptId);
  }

  async getPendingRequests(): Promise<ScriptApprovalRequest[]> {
    return Array.from(this.pendingRequests.values());
  }

  async revoke(scriptId: string): Promise<void> {
    this.approvals.delete(scriptId);
    this.pendingRequests.delete(scriptId);
  }
}

/**
 * Approval workflow manager
 */
export class ApprovalWorkflow {
  constructor(private store: IApprovalStore = new InMemoryApprovalStore()) {}

  /**
   * Check if script execution is approved (for trustLevel 2)
   */
  async canExecute(scriptId: string): Promise<boolean> {
    return this.store.isApproved(scriptId);
  }

  /**
   * Submit script for approval
   */
  async submitForApproval(
    scriptId: string,
    scriptName: string,
    code: string,
    requestedBy?: string,
    reason?: string
  ): Promise<void> {
    await this.store.requestApproval({
      scriptId,
      scriptName,
      code,
      requestedAt: Date.now(),
      requestedBy,
      reason,
    });
  }

  /**
   * Approve script
   */
  async approveScript(
    scriptId: string,
    approvedBy: string,
    notes?: string,
    expiresInMs?: number
  ): Promise<void> {
    await this.store.approve(scriptId, approvedBy, notes, expiresInMs);
  }

  /**
   * Reject script
   */
  async rejectScript(
    scriptId: string,
    approvedBy: string,
    notes?: string
  ): Promise<void> {
    await this.store.reject(scriptId, approvedBy, notes);
  }

  /**
   * Get approval status
   */
  async getStatus(
    scriptId: string
  ): Promise<{ approved: boolean; details?: ScriptApproval }> {
    const approved = await this.store.isApproved(scriptId);
    const details = await this.store.getApproval(scriptId);
    return { approved, details: details || undefined };
  }

  /**
   * Get pending requests
   */
  async getPendingRequests(): Promise<ScriptApprovalRequest[]> {
    return this.store.getPendingRequests();
  }

  /**
   * Revoke approval
   */
  async revokeApproval(scriptId: string): Promise<void> {
    await this.store.revoke(scriptId);
  }
}
